import { sectionRenderer } from '@theme/section-renderer';

if (!window.ESSNTLCartVariantEditor?.initialized) {
  const translations = {
    editItem: 'Edit item',
    editSize: 'Edit size',
    editColor: 'Edit color',
    size: 'Size',
    setSize: 'Set size',
    color: 'Color',
    includesPixelSet: 'Includes shirt + shorts',
    includesPixelShell: 'Includes jacket + pants',
    updateItem: 'Update item',
    updatingItem: 'Updating…',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading',
    soldOut: 'Sold out',
    unavailable: 'This combination is sold out.',
    loadError: 'Could not load available options. Please try again.',
    error: 'Could not update this item. Please try again.',
    ...(window.ESSNTLCartVariantEditorTranslations || {}),
  };

  const rootRoute = window.Shopify?.routes?.root || '/';
  const productCache = new Map();
  const variantCache = new Map();
  let catalogPromise;
  let dialog;
  let state;
  let sessionId = 0;

  function route(path) {
    return rootRoute.endsWith('/') ? `${rootRoute}${path}` : `${rootRoute}/${path}`;
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function safeJSON(value, fallback) {
    try {
      return JSON.parse(value || '');
    } catch (_) {
      return fallback;
    }
  }

  function titleFor(resource) {
    return String(resource?.product_title || resource?.title || '').trim();
  }

  function handleFor(resource) {
    return String(resource?.handle || resource?.product_handle || '').trim();
  }

  function familyFor(resource) {
    const signal = `${handleFor(resource)} ${titleFor(resource)} ${resource?.product_type || resource?.type || ''}`.toLowerCase();
    // Pixel Shell is checked first: it shares the word "Pixel" with the cotton
    // Pixel Set but is a separate family with its own colourways.
    if (signal.includes('pixel shell') || signal.includes('pixel-shell') || signal.includes('pixel_shell')) return 'pixel_shell';
    if (signal.includes('pixel set') || signal.includes('pixel-set') || signal.includes('pixel_set')) return 'pixel_set';
    if (signal.includes('tracksuit') || signal.includes('track suit')) return 'tracksuit';
    if (signal.includes('zip hoodie') || signal.includes('zip-hoodie')) return 'zip_hoodie';
    if (signal.includes('hoodie')) return 'hoodie';
    if (signal.includes('sweat')) return 'sweats';
    if (signal.includes('shorts')) {
      if (signal.includes('summer') || signal.includes('knit') || signal.includes('textured')) return '';
      return 'shorts';
    }
    return '';
  }

  /* Which naming line a product is in. Mirrors snippets/essntl-product-line.liquid:
     "ESSNTL MIDNIGHT HOODIE" is a signature colourway, "Hoodie In Cloud" is a
     classic one, and the two must never be offered as swaps for each other. */
  function lineFor(resource) {
    const title = titleFor(resource).toLowerCase().trim();
    if (title.includes(' in ')) return 'classic';
    const words = title.split(/\s+/);
    const garments = ['tracksuit', 'hoodie', 'sweatpants', 'sweats', 'shorts', 'sweatshirt'];
    if (words.length >= 3 && words[0] === 'essntl' && garments.includes(words[words.length - 1])) {
      return 'signature';
    }
    return 'classic';
  }

  function genderFor(resource) {
    return /(^|\s)woman(\s|$)/i.test(titleFor(resource).replace(/[-_/.,:;!?()[\]]/g, ' ')) ? 'woman' : 'man';
  }

  function itemOptions(item) {
    if (Array.isArray(item?.options_with_values)) return item.options_with_values;
    if (Array.isArray(item?.variant_options)) {
      return item.variant_options.map((value, index) => ({ name: `Option ${index + 1}`, value }));
    }
    return [];
  }

  function findNamedOption(options, matcher) {
    return options.find((option) => matcher.test(String(option?.name || '')));
  }

  function colorwayFor(resource) {
    const options = itemOptions(resource);
    const optionColor = findNamedOption(options, /colou?r/i)?.value;
    if (optionColor) return String(optionColor).trim();

    const family = familyFor(resource);
    const title = titleFor(resource).replace(/\s+Woman\s*$/i, '').trim();
    const inMatch = title.match(/\s+In\s+(.+)$/i);
    if (inMatch?.[1]) return inMatch[1].trim();

    /* "ESSNTL MIDNIGHT HOODIE" -> "Midnight": the colour sits between the brand
       word and the garment noun rather than after "In". */
    if (lineFor(resource) === 'signature') {
      const words = title.split(/\s+/);
      const colour = words.slice(1, -1).join(' ').trim();
      if (colour) return colour.charAt(0).toUpperCase() + colour.slice(1).toLowerCase();
    }
    if (handleFor(resource) === 'tracksuit-woman') return 'Cloud';
    if (family === 'pixel_set') {
      return title.replace(/^ESSNTL\s+/i, '').replace(/^Pixel\s+Set\s+/i, '').trim();
    }
    return '';
  }

  function prettyColor(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function isProtectionItem(item) {
    return handleFor(item) === 'shipping-protection' || Boolean(item?.properties?._essntl_shipping_protection);
  }

  function updateSizeProperties(properties, size) {
    const result = { ...(properties || {}) };
    if (!size) return result;
    for (const key of Object.keys(result)) {
      const normalized = key.trim().toLowerCase();
      if (normalized === 'top size' || normalized === 'bottom size' || normalized === 'set size') {
        result[key] = size;
      }
    }
    return result;
  }

  function buildItemDetails(item) {
    if (!item || isProtectionItem(item)) return '';

    const options = itemOptions(item);
    const family = familyFor(item);
    const size = findNamedOption(options, /size/i)?.value || '';
    const optionColor = findNamedOption(options, /colou?r/i)?.value || '';
    const color = optionColor || colorwayFor(item);
    const summaryParts = [];

    const isCompleteSet = family === 'pixel_set' || family === 'pixel_shell';
    if (size) summaryParts.push(`${isCompleteSet ? translations.setSize : translations.size}: ${size}`);
    if (color) summaryParts.push(prettyColor(color));

    const meta = [];
    if (summaryParts.length) {
      meta.push(`<span class="essntl-cart-item-meta__primary">${escapeHTML(summaryParts.join(' · '))}</span>`);
    }
    if (family === 'pixel_shell') {
      meta.push(`<span class="essntl-cart-item-meta__secondary">${escapeHTML(translations.includesPixelShell)}</span>`);
    } else if (family === 'pixel_set') {
      meta.push(`<span class="essntl-cart-item-meta__secondary">${escapeHTML(translations.includesPixelSet)}</span>`);
    }

    const additional = [];
    for (const option of options) {
      const name = String(option?.name || '');
      const value = String(option?.value || '');
      if (!value || value === 'Default Title' || /size|colou?r/i.test(name)) continue;
      additional.push(`${escapeHTML(name)}: ${escapeHTML(value)}`);
    }
    for (const [key, value] of Object.entries(item.properties || {})) {
      if (!value || key.startsWith('_') || /^(top|bottom|set) size$/i.test(key.trim())) continue;
      additional.push(`${escapeHTML(key)}: ${escapeHTML(value)}`);
    }

    const handle = handleFor(item);
    const canEditSize = Boolean(size);
    const canEditColor = Boolean(optionColor || (family && color));
    const canEdit = handle && !item.item_components?.length && !item.parent_relationship?.parent;
    const sharedData = [
      `data-line-key="${escapeHTML(item.key || '')}"`,
      `data-product-handle="${escapeHTML(handle)}"`,
      `data-product-title="${escapeHTML(titleFor(item))}"`,
      `data-variant-id="${escapeHTML(item.variant_id || item.id || '')}"`,
      `data-quantity="${escapeHTML(item.quantity || 1)}"`,
      `data-current-options="${escapeHTML(JSON.stringify(item.variant_options || options.map((option) => option.value)))}"`,
      `data-item-properties="${escapeHTML(JSON.stringify(item.properties || {}))}"`,
      `data-selling-plan-id="${escapeHTML(item.selling_plan_allocation?.selling_plan?.id || '')}"`,
      'aria-haspopup="dialog"',
    ].join(' ');

    const edits = [];
    if (canEdit && canEditSize) {
      edits.push(`<button type="button" class="essntl-cart-item-edit" data-cart-variant-edit data-option-name="size" ${sharedData}>${escapeHTML(translations.editSize)}</button>`);
    }
    if (canEdit && canEditColor) {
      edits.push(`<button type="button" class="essntl-cart-item-edit" data-cart-variant-edit data-option-name="color" ${sharedData}>${escapeHTML(translations.editColor)}</button>`);
    }

    if (!meta.length && !additional.length && !edits.length) return '';

    return [
      '<div class="essntl-cart-item-details">',
      meta.length ? `<div class="essntl-cart-item-meta">${meta.join('')}</div>` : '',
      additional.length ? `<div class="essntl-cart-item-meta essntl-cart-item-meta--additional"><span>${additional.join('</span><span>')}</span></div>` : '',
      edits.length ? `<div class="essntl-cart-item-edits" aria-label="${escapeHTML(translations.editItem)}">${edits.join('')}</div>` : '',
      '</div>',
    ].join('');
  }

  function normalizedOptionValue(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function sameOptionValue(first, second) {
    return normalizedOptionValue(first) === normalizedOptionValue(second);
  }

  function rawOptionDefinitions(product) {
    return (product?.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : (option?.name || `Option ${index + 1}`),
      position: Number(typeof option === 'string' ? index + 1 : (option?.position || index + 1)),
      values: typeof option === 'string' ? [] : (Array.isArray(option?.values) ? option.values.map(String) : []),
    }));
  }

  function variantOptionValues(variant, definitions) {
    if (Array.isArray(variant?.options)) return variant.options.map(String);
    return definitions.map((option, index) => {
      const position = Number(option.position || index + 1);
      return String(variant?.[`option${position}`] ?? variant?.[`option${index + 1}`] ?? '');
    });
  }

  function normalizeProduct(product) {
    if (!product) return product;
    const definitions = rawOptionDefinitions(product);
    const variants = (product.variants || []).map((variant) => ({
      ...variant,
      options: variantOptionValues(variant, definitions),
    }));
    const options = definitions.map((option, index) => ({
      ...option,
      values: option.values.length
        ? option.values
        : [...new Set(variants.map((variant) => variant.options[index]).filter(Boolean))],
    }));
    return { ...product, options, variants };
  }

  function optionDefinitions(product) {
    const definitions = rawOptionDefinitions(product);
    return definitions.map((option, index) => ({
      ...option,
      values: option.values.length
        ? option.values
        : [...new Set((product?.variants || []).map((variant) => variantOptionValues(variant, definitions)[index]).filter(Boolean))],
    }));
  }

  function sizeIndex(product) {
    return optionDefinitions(product).findIndex((option) => /size/i.test(option.name));
  }

  function colorIndex(product) {
    return optionDefinitions(product).findIndex((option) => /colou?r/i.test(option.name));
  }

  function cacheProduct(product) {
    const normalizedProduct = normalizeProduct(product);
    if (!normalizedProduct?.handle) return normalizedProduct;
    productCache.set(normalizedProduct.handle, normalizedProduct);
    for (const variant of normalizedProduct.variants || []) variantCache.set(String(variant.id), variant);
    return normalizedProduct;
  }

  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
    });
    let responseBody = '';
    let payload = null;
    try {
      responseBody = await response.text();
      payload = responseBody ? JSON.parse(responseBody) : null;
    } catch (_) {
      payload = null;
    }
    if (!response.ok) {
      const description = payload?.description || payload?.message || payload?.status;
      const error = new Error(description || `Request failed (${response.status})`);
      error.status = response.status;
      error.endpoint = url;
      error.payload = payload || responseBody;
      let requestPayload = options.body || null;
      try {
        requestPayload = options.body ? JSON.parse(options.body) : null;
      } catch (_) {
        // Keep a non-JSON body as-is for diagnostics.
      }
      console.error('[ESSNTL cart variant editor] Shopify request failed', {
        endpoint: url,
        method: options.method || 'GET',
        status: response.status,
        request: requestPayload,
        response: payload || responseBody,
      });
      throw error;
    }
    return payload;
  }

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    return cacheProduct(await fetchJSON(route(`products/${encodeURIComponent(handle)}.js`)));
  }

  async function getCatalog() {
    if (!catalogPromise) {
      catalogPromise = fetchJSON(route('products.json?limit=250')).then((payload) => payload.products || []);
    }
    return catalogPromise;
  }

  async function getColorways(product) {
    const family = familyFor(product);
    if (!family || colorIndex(product) >= 0) return [];

    const catalog = await getCatalog();
    const gender = genderFor(product);
    const line = lineFor(product);
    const handles = new Set([product.handle]);
    const seenColors = new Set([normalizedOptionValue(colorwayFor(product))].filter(Boolean));

    for (const candidate of catalog) {
      if (familyFor(candidate) !== family || genderFor(candidate) !== gender) continue;
      if (lineFor(candidate) !== line) continue;
      const handle = handleFor(candidate);
      const color = normalizedOptionValue(colorwayFor(candidate));
      if (!handle || !color || seenColors.has(color)) continue;
      handles.add(handle);
      seenColors.add(color);
    }

    const results = await Promise.allSettled(Array.from(handles).map((handle) => getProduct(handle)));
    const colorways = [];
    const canonicalColors = new Set();
    for (const result of results) {
      if (result.status !== 'fulfilled' || !result.value) {
        if (result.status === 'rejected') console.warn('[ESSNTL cart variant editor] colorway was skipped', result.reason);
        continue;
      }
      const color = normalizedOptionValue(colorwayFor(result.value));
      if (!color || canonicalColors.has(color)) continue;
      canonicalColors.add(color);
      colorways.push(result.value);
    }
    return colorways.sort((first, second) => colorwayFor(first).localeCompare(colorwayFor(second)));
  }

  function createDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'essntl-cart-variant-dialog';
    dialog.setAttribute('aria-labelledby', 'essntl-cart-variant-dialog-title');
    dialog.innerHTML = `
      <div class="essntl-cart-variant-dialog__panel">
        <header class="essntl-cart-variant-dialog__header">
          <div>
            <span class="essntl-cart-variant-dialog__eyebrow">${escapeHTML(translations.editItem)}</span>
            <h2 class="essntl-cart-variant-dialog__title" id="essntl-cart-variant-dialog-title"></h2>
          </div>
          <button type="button" class="essntl-cart-variant-dialog__close" data-cart-variant-close aria-label="${escapeHTML(translations.close)}">×</button>
        </header>
        <div class="essntl-cart-variant-dialog__body">
          <div class="essntl-cart-variant-dialog__loading" data-cart-variant-loading>${escapeHTML(translations.loading)}</div>
          <div class="essntl-cart-variant-dialog__options" data-cart-variant-options hidden></div>
          <p class="essntl-cart-variant-dialog__selection" data-cart-variant-selection hidden></p>
          <p class="essntl-cart-variant-dialog__error" data-cart-variant-error role="alert" hidden></p>
        </div>
        <footer class="essntl-cart-variant-dialog__footer">
          <button type="button" class="essntl-cart-variant-dialog__button" data-cart-variant-close>${escapeHTML(translations.cancel)}</button>
          <button type="button" class="essntl-cart-variant-dialog__button essntl-cart-variant-dialog__button--primary" data-cart-variant-save disabled>${escapeHTML(translations.updateItem)}</button>
        </footer>
      </div>`;
    document.body.appendChild(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-cart-variant-close]')) closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.querySelector('[data-cart-variant-save]')?.addEventListener('click', saveSelection);
    return dialog;
  }

  function closeDialog() {
    sessionId += 1;
    if (dialog?.open) dialog.close();
    document.body.classList.remove('essntl-cart-variant-dialog-open');
    state = undefined;
  }

  function showError(message) {
    const error = dialog?.querySelector('[data-cart-variant-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function setLoading(loading) {
    if (!dialog) return;
    dialog.querySelector('[data-cart-variant-loading]')?.toggleAttribute('hidden', !loading);
    dialog.querySelector('[data-cart-variant-options]')?.toggleAttribute('hidden', loading);
    dialog.querySelector('[data-cart-variant-selection]')?.toggleAttribute('hidden', loading);
    dialog.toggleAttribute('data-loading', loading);
    if (loading) dialog.setAttribute('aria-busy', 'true');
    else if (!dialog.hasAttribute('data-saving')) dialog.removeAttribute('aria-busy');
  }

  function showLoadFailure(message) {
    setLoading(false);
    dialog?.querySelector('[data-cart-variant-options]')?.setAttribute('hidden', '');
    dialog?.querySelector('[data-cart-variant-selection]')?.setAttribute('hidden', '');
    showError(message);
  }

  function selectedVariant() {
    if (!state?.product) return null;
    return (state.product.variants || []).find((variant) =>
      variant.options?.every((value, index) => sameOptionValue(value, state.selectedOptions[index]))
    ) || null;
  }

  function variantAvailableWith(product, selection, optionIndex, value) {
    return (product.variants || []).some((variant) => {
      if (!variant.available || !sameOptionValue(variant.options?.[optionIndex], value)) return false;
      return variant.options?.every((variantValue, index) => {
        if (index === optionIndex || !selection[index]) return true;
        return sameOptionValue(variantValue, selection[index]);
      });
    });
  }

  function selectionByOptionName(product, selection) {
    return new Map(optionDefinitions(product).map((option, index) => [normalizedOptionValue(option.name), selection[index]]));
  }

  function colorwayHasSelection(product, selectedByName) {
    const definitions = optionDefinitions(product);
    return (product.variants || []).some((variant) => {
      if (!variant.available) return false;
      return definitions.every((option, index) => {
        if (/colou?r/i.test(option.name)) return true;
        const selected = selectedByName.get(normalizedOptionValue(option.name));
        return !selected || sameOptionValue(variant.options?.[index], selected);
      });
    });
  }

  function swatchColor(value) {
    const key = String(value || '').toLowerCase();
    const known = {
      black: '#141414',
      night: '#1d2028',
      grey: '#9b9b96',
      gray: '#9b9b96',
      cloud: '#d7d8d5',
      blue: '#5f7f9f',
      sea: '#7fa4a8',
      dune: '#c8b69a',
      blossom: '#d9aab5',
    };
    return known[key] || '#c7c7c9';
  }

  function optionButton({ label, value, selected, disabled, optionIndex, color, colorwayHandle }) {
    const soldOutLabel = disabled ? ` — ${translations.soldOut}` : '';
    return `
      <button
        type="button"
        class="essntl-cart-variant-value"
        data-cart-variant-value="${escapeHTML(value)}"
        ${optionIndex !== undefined ? `data-option-index="${optionIndex}"` : ''}
        ${colorwayHandle ? `data-colorway-handle="${escapeHTML(colorwayHandle)}"` : ''}
        aria-pressed="${selected ? 'true' : 'false'}"
        aria-label="${escapeHTML(label + soldOutLabel)}"
        ${disabled ? 'disabled' : ''}
      >
        ${color ? `<span class="essntl-cart-variant-value__swatch" style="--essntl-cart-swatch:${swatchColor(label)}"></span>` : ''}
        <span>${escapeHTML(label)}</span>
      </button>`;
  }

  function renderEditor() {
    if (!dialog || !state?.product) return;
    const optionsContainer = dialog.querySelector('[data-cart-variant-options]');
    const selectionText = dialog.querySelector('[data-cart-variant-selection]');
    const saveButton = dialog.querySelector('[data-cart-variant-save]');
    const definitions = optionDefinitions(state.product);
    const groups = [];

    definitions.forEach((option, index) => {
      if (option.values.length <= 1 && !/size|colou?r/i.test(option.name)) return;
      const isColor = /colou?r/i.test(option.name);
      const isSize = /size/i.test(option.name);
      const editorFamily = familyFor(state.product);
      const editorIsSet = editorFamily === 'pixel_set' || editorFamily === 'pixel_shell';
      const label = isSize && editorIsSet ? translations.setSize : (isColor ? translations.color : option.name);
      const buttons = option.values.map((value) => optionButton({
        label: value,
        value,
        selected: sameOptionValue(state.selectedOptions[index], value),
        disabled: !variantAvailableWith(state.product, state.selectedOptions, index, value),
        optionIndex: index,
        color: isColor,
      })).join('');
      groups.push(`<fieldset class="essntl-cart-variant-option" data-option-type="${isSize ? 'size' : (isColor ? 'color' : 'other')}"><legend class="essntl-cart-variant-option__legend">${escapeHTML(label)}</legend><div class="essntl-cart-variant-option__values">${buttons}</div></fieldset>`);
    });

    if (state.colorways.length > 1 && colorIndex(state.product) < 0) {
      const selectedByName = selectionByOptionName(state.product, state.selectedOptions);
      const buttons = state.colorways.map((product) => {
        const color = colorwayFor(product);
        return optionButton({
          label: prettyColor(color),
          value: color,
          selected: product.handle === state.product.handle,
          disabled: !colorwayHasSelection(product, selectedByName),
          color: true,
          colorwayHandle: product.handle,
        });
      }).join('');
      groups.push(`<fieldset class="essntl-cart-variant-option" data-option-type="color"><legend class="essntl-cart-variant-option__legend">${escapeHTML(translations.color)}</legend><div class="essntl-cart-variant-option__values">${buttons}</div></fieldset>`);
    }

    optionsContainer.innerHTML = groups.join('');
    optionsContainer.querySelectorAll('[data-option-index]').forEach((button) => {
      button.addEventListener('click', () => {
        state.selectedOptions[Number(button.dataset.optionIndex)] = button.dataset.cartVariantValue;
        showError('');
        renderEditor();
      });
    });
    optionsContainer.querySelectorAll('[data-colorway-handle]').forEach((button) => {
      button.addEventListener('click', () => selectColorway(button.dataset.colorwayHandle));
    });

    const target = selectedVariant();
    const sizeValue = sizeIndex(state.product) >= 0 ? state.selectedOptions[sizeIndex(state.product)] : '';
    const colorValue = colorIndex(state.product) >= 0 ? state.selectedOptions[colorIndex(state.product)] : colorwayFor(state.product);
    const summary = [];
    const summaryFamily = familyFor(state.product);
    const summaryIsSet = summaryFamily === 'pixel_set' || summaryFamily === 'pixel_shell';
    if (sizeValue) summary.push(`${summaryIsSet ? translations.setSize : translations.size}: ${sizeValue}`);
    if (colorValue) summary.push(`${translations.color}: ${prettyColor(colorValue)}`);
    selectionText.textContent = summary.join(' · ');
    saveButton.disabled = !target?.available || String(target.id) === String(state.currentVariantId);
    showError(target?.available === false ? translations.unavailable : '');
  }

  async function selectColorway(handle) {
    if (!state || !handle || handle === state.product.handle) return;
    const activeState = state;
    const activeSession = activeState.sessionId;
    const previousDefinitions = optionDefinitions(state.product);
    const previousByName = new Map(previousDefinitions.map((option, index) => [normalizedOptionValue(option.name), state.selectedOptions[index]]));
    setLoading(true);
    showError('');
    try {
      const product = await getProduct(handle);
      if (state !== activeState || state?.sessionId !== activeSession) return;
      const definitions = optionDefinitions(product);
      const nextOptions = definitions.map((option, index) => {
        const previous = previousByName.get(normalizedOptionValue(option.name));
        const matchingValue = option.values.find((value) => previous && sameOptionValue(value, previous));
        if (matchingValue) return matchingValue;
        return (product.variants || []).find((variant) => variant.available)?.options?.[index] || option.values[0] || '';
      });
      state.product = product;
      state.selectedOptions = nextOptions;
      renderEditor();
    } catch (error) {
      if (state !== activeState || state?.sessionId !== activeSession) return;
      console.error('[ESSNTL cart variant editor] colorway load error', error);
      showError(translations.error);
    } finally {
      if (state === activeState && state?.sessionId === activeSession) setLoading(false);
    }
  }

  async function openEditor(trigger) {
    const modal = createDialog();
    const activeSession = ++sessionId;
    state = {
      sessionId: activeSession,
      trigger,
      lineKey: trigger.dataset.lineKey || '',
      currentHandle: trigger.dataset.productHandle || '',
      currentVariantId: trigger.dataset.variantId || '',
      requestedFocus: trigger.dataset.optionName || '',
      properties: safeJSON(trigger.dataset.itemProperties, {}),
      sellingPlanId: trigger.dataset.sellingPlanId || '',
      product: null,
      selectedOptions: safeJSON(trigger.dataset.currentOptions, []),
      colorways: [],
    };

    modal.querySelector('.essntl-cart-variant-dialog__title').textContent = trigger.dataset.productTitle || translations.editItem;
    modal.querySelector('[data-cart-variant-save]').textContent = translations.updateItem;
    modal.querySelector('[data-cart-variant-save]').disabled = true;
    showError('');
    setLoading(true);
    if (!modal.open) modal.showModal();
    document.body.classList.add('essntl-cart-variant-dialog-open');

    try {
      const activeState = state;
      const product = await getProduct(activeState.currentHandle);
      if (state !== activeState || state?.sessionId !== activeSession) return;
      const currentVariant = (product.variants || []).find((variant) => String(variant.id) === String(state.currentVariantId));
      if (!currentVariant) throw new Error(`Variant ${state.currentVariantId} was not found in ${state.currentHandle}`);
      state.product = product;
      state.selectedOptions = currentVariant.options.slice();
      state.colorways = await getColorways(product);
      if (state !== activeState || state?.sessionId !== activeSession) return;
      renderEditor();
      setLoading(false);

      requestAnimationFrame(() => {
        if (state !== activeState || state?.sessionId !== activeSession) return;
        modal.querySelector('[data-cart-variant-close]')?.focus({ preventScroll: true });
      });
    } catch (error) {
      if (state?.sessionId !== activeSession) return;
      console.error('[ESSNTL cart variant editor] open error', error);
      showLoadFailure(translations.loadError);
    }
  }

  function stableProperties(properties) {
    return JSON.stringify(
      Object.entries(properties || {})
        .filter(([, value]) => value !== null && value !== '')
        .map(([key, value]) => [String(key), String(value)])
        .sort(([first], [second]) => first.localeCompare(second))
    );
  }

  function sellingPlanId(item) {
    return String(item?.selling_plan_allocation?.selling_plan?.id || '');
  }

  async function changeLine(key, quantity) {
    return fetchJSON(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  function findCartLine(cart, identity, excludedKeys = new Set()) {
    const items = (cart?.items || []).filter((item) => !excludedKeys.has(item.key));
    const exact = items.find((item) => item.key === identity.key);
    if (exact) return exact;

    const matches = items.filter((item) =>
      String(item.variant_id) === String(identity.variantId) &&
      stableProperties(item.properties) === identity.propertiesKey &&
      sellingPlanId(item) === identity.sellingPlanId
    );
    return matches.length === 1 ? matches[0] : null;
  }

  function targetLineFromAddResponse(addResponse, cart, identity, oldIdentity) {
    const addedItems = Array.isArray(addResponse?.items) ? addResponse.items : [addResponse];
    const responseLine = addedItems.find((item) =>
      String(item?.variant_id || item?.id) === String(identity.variantId) &&
      stableProperties(item?.properties) === identity.propertiesKey
    );
    if (responseLine?.key) {
      const exact = (cart.items || []).find((item) => item.key === responseLine.key);
      if (exact) return exact;
    }

    const excludedKeys = new Set();
    const oldAfterAdd = findCartLine(cart, oldIdentity);
    if (oldAfterAdd?.key) excludedKeys.add(oldAfterAdd.key);
    return findCartLine(cart, identity, excludedKeys);
  }

  async function replaceLine(targetVariant) {
    const activeState = state;
    const before = await fetchJSON(route('cart.js'));
    if (state !== activeState) throw new Error('Editor session changed while updating');
    const oldIdentity = {
      key: state.lineKey,
      variantId: state.currentVariantId,
      propertiesKey: stableProperties(state.properties),
      sellingPlanId: String(state.sellingPlanId || ''),
    };
    const oldLine = findCartLine(before, oldIdentity);
    if (!oldLine) throw new Error('Original cart line no longer exists');

    oldIdentity.key = oldLine.key;
    oldIdentity.variantId = oldLine.variant_id;
    oldIdentity.propertiesKey = stableProperties(oldLine.properties);
    oldIdentity.sellingPlanId = sellingPlanId(oldLine);

    const targetSizeIndex = sizeIndex(state.product);
    const targetSize = targetSizeIndex >= 0 ? targetVariant.options?.[targetSizeIndex] : '';
    const properties = updateSizeProperties(oldLine.properties || state.properties, targetSize);
    const planId = sellingPlanId(oldLine) || state.sellingPlanId;
    const propertiesKey = stableProperties(properties);
    const targetIdentity = {
      key: '',
      variantId: targetVariant.id,
      propertiesKey,
      sellingPlanId: String(planId || ''),
    };
    const existingTarget = findCartLine(before, targetIdentity, new Set([oldLine.key]));
    const previousTargetQuantity = existingTarget?.quantity || 0;
    const addItem = { id: Number(targetVariant.id), quantity: oldLine.quantity };
    if (Object.keys(properties).length) addItem.properties = properties;
    if (planId) addItem.selling_plan = Number(planId);

    const addResponse = await fetchJSON(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [addItem] }),
    });

    const afterAdd = await fetchJSON(route('cart.js'));
    const targetLine = targetLineFromAddResponse(addResponse, afterAdd, targetIdentity, oldIdentity);
    if (!targetLine) throw new Error('Target cart line was not created');
    const sourceAfterAdd = findCartLine(afterAdd, oldIdentity, new Set([targetLine.key]));
    if (!sourceAfterAdd) {
      try {
        await changeLine(targetLine.key, previousTargetQuantity);
      } catch (rollbackError) {
        console.error('[ESSNTL cart variant editor] rollback error', rollbackError);
      }
      throw new Error('Original cart line changed before it could be replaced');
    }

    try {
      return await changeLine(sourceAfterAdd.key, 0);
    } catch (error) {
      try {
        const rollbackCart = await fetchJSON(route('cart.js'));
        const rollbackTarget = findCartLine(rollbackCart, {
          ...targetIdentity,
          key: targetLine.key,
        }, new Set([sourceAfterAdd.key]));
        if (rollbackTarget) await changeLine(rollbackTarget.key, previousTargetQuantity);
      } catch (rollbackError) {
        console.error('[ESSNTL cart variant editor] rollback error', rollbackError);
      }
      throw error;
    }
  }

  function updateHeaderCount(cart) {
    document.querySelectorAll('[data-essntl-cart-count], .cart-bubble__text-count, [data-cart-count], .cart-count, #CartCount, [id*="cart-count"]').forEach((element) => {
      element.textContent = cart.item_count;
      element.toggleAttribute('hidden', cart.item_count === 0);
    });
  }

  async function refreshNativeCartSections() {
    const sectionIds = new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
    );
    await Promise.allSettled(
      Array.from(sectionIds).map((sectionId) => sectionRenderer.renderSection(sectionId, { cache: false, mode: 'full' }))
    );
  }

  async function saveSelection() {
    const target = selectedVariant();
    const saveButton = dialog?.querySelector('[data-cart-variant-save]');
    if (!state || !target?.available || !saveButton || saveButton.disabled) return;
    const activeState = state;

    saveButton.disabled = true;
    saveButton.textContent = translations.updatingItem;
    dialog.setAttribute('data-saving', '');
    dialog.setAttribute('aria-busy', 'true');
    showError('');

    try {
      variantCache.set(String(target.id), target);
      const cart = await replaceLine(target);
      if (state !== activeState) return;
      const updatedProduct = state.product;
      dialog.removeAttribute('data-saving');
      dialog.removeAttribute('aria-busy');
      saveButton.textContent = translations.updateItem;
      if (dialog.open) dialog.close();
      document.body.classList.remove('essntl-cart-variant-dialog-open');
      updateHeaderCount(cart);
      document.dispatchEvent(new CustomEvent('essntl:cart:variant-updated', {
        detail: { cart, variant: target, product: updatedProduct },
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      await refreshNativeCartSections();
      if (state === activeState) state = undefined;
    } catch (error) {
      if (state !== activeState) return;
      console.error('[ESSNTL cart variant editor] update error', error);
      showError(error?.status === 422 ? translations.unavailable : translations.error);
      saveButton.disabled = !selectedVariant()?.available;
    } finally {
      if (state === activeState) {
        dialog.removeAttribute('data-saving');
        dialog.removeAttribute('aria-busy');
        saveButton.textContent = translations.updateItem;
      }
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-cart-variant-edit]');
    if (!(trigger instanceof HTMLElement)) return;
    event.preventDefault();
    openEditor(trigger);
  });

  window.ESSNTLCartVariantEditor = {
    initialized: true,
    buildItemDetails,
    getVariantPricing(variantId) {
      return variantCache.get(String(variantId)) || null;
    },
  };
}